import { FUNNEL_STAGES, FUNNEL_STAGE_LABELS, formatDateOnly } from '@dataroom/shared';
import type { BuyerEngagementSummary, FunnelStage, LoiReviewItem } from '@dataroom/shared';

/** The stage bar's short segment labels, in funnel order. */
export const PIPELINE_SEGMENTS: Record<FunnelStage, string> = {
  teaser: 'Teaser',
  nda_signed: 'NDA',
  cim: 'CIM',
  loi_submitted: 'LOI',
  due_diligence: 'DD',
  exclusivity_expires: 'Excl.',
  closing: 'Close',
};

/** 1-based position of a stage in the 7-step funnel. */
export function stageNumber(stage: FunnelStage): number {
  return FUNNEL_STAGES.indexOf(stage) + 1;
}

/** "Stage 3 of 7: CIM". */
export function stageLine(stage: FunnelStage): string {
  return `Stage ${stageNumber(stage)} of ${FUNNEL_STAGES.length}: ${FUNNEL_STAGE_LABELS[stage]}`;
}

/** Each buyer's latest LOI (by submission time), keyed by buyer. */
export function latestLoiByBuyer(lois: readonly LoiReviewItem[]): Map<string, LoiReviewItem> {
  const out = new Map<string, LoiReviewItem>();
  for (const l of lois) {
    const cur = out.get(l.actorId);
    if (!cur || Date.parse(l.submittedAt) > Date.parse(cur.submittedAt)) out.set(l.actorId, l);
  }
  return out;
}

/**
 * The one-line state of a buyer, from recorded facts only — their funnel stage,
 * their latest LOI's recorded status, whether their access ended, and whether
 * they have opened anything. No prediction, no judgment. Pure.
 */
export function buyerStateLine(
  b: Pick<BuyerEngagementSummary, 'stage' | 'accessEnded' | 'documentsOpened'>,
  loi: Pick<LoiReviewItem, 'status' | 'exclusivityExpiresAt' | 'closingDate'> | undefined,
): string {
  if (b.accessEnded === 'revoked') return 'Access revoked';
  if (b.accessEnded === 'expired') return 'Access expired';
  switch (b.stage) {
    case 'teaser':
      return 'Teaser only';
    case 'nda_signed':
      return 'NDA signed — CIM not unlocked yet';
    case 'cim':
      return b.documentsOpened > 0 ? 'Reading the CIM' : 'CIM unlocked — nothing opened yet';
    case 'loi_submitted':
      switch (loi?.status) {
        case 'redlined':
          return 'LOI countered';
        case 'rejected':
          return 'LOI rejected';
        case 'accepted':
        case 'accepted_signed':
          return 'LOI accepted';
        case 'submitted':
          return 'LOI submitted — awaiting your response';
        default:
          return 'LOI stage';
      }
    case 'due_diligence':
      return 'In due diligence';
    case 'exclusivity_expires':
      return loi?.exclusivityExpiresAt
        ? `Exclusive until ${formatDateOnly(loi.exclusivityExpiresAt)}`
        : 'In exclusivity';
    case 'closing':
      return loi?.closingDate ? `Closing ${formatDateOnly(loi.closingDate)}` : 'Closing';
  }
}

/** How a buyer is named: their display name (person or firm), else the email. */
export function buyerLabel(
  b: Pick<BuyerEngagementSummary, 'actorName' | 'actorEmail' | 'actorId'>,
): string {
  return b.actorName?.trim() || b.actorEmail || b.actorId;
}

/** Furthest stage first, then the most recent activity, then by email. Pure. */
export function sortPipeline<
  T extends Pick<
    BuyerEngagementSummary,
    'stage' | 'lastActiveAt' | 'actorEmail' | 'actorId' | 'actorName'
  >,
>(
  buyers: readonly T[],
): T[] {
  const at = (s: string | null) => (s ? Date.parse(s) : -Infinity);
  return [...buyers].sort(
    (a, b) =>
      stageNumber(b.stage) - stageNumber(a.stage) ||
      at(b.lastActiveAt) - at(a.lastActiveAt) ||
      buyerLabel(a).localeCompare(buyerLabel(b)),
  );
}
